import React, { useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
} from "recharts";
import { getMarketChart } from "../lib/api";

const TIMEFRAMES = ["1D", "1W", "1M", "3M", "6M", "1Y", "5Y", "MAX"];

const ChartTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="rounded-lg border border-white/15 bg-[#181B21]/95 p-3 shadow-2xl backdrop-blur-md">
      <div className="mb-1.5 font-mono text-[10px] uppercase tracking-wider text-zinc-500">Change from start</div>
      <div className="space-y-1">
        {payload.map((p) => (
          <div key={p.dataKey} className="flex items-center justify-between gap-4">
            <span className="flex items-center gap-1.5 text-xs text-zinc-300">
              <span className="h-2 w-2 rounded-full" style={{ background: p.color }} />
              {p.dataKey}
            </span>
            <span className="font-mono text-xs font-medium tabular-nums" style={{ color: p.color }}>
              {p.value > 0 ? "+" : ""}{p.value.toFixed(2)}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export const MarketChart = () => {
  const [timeframe, setTimeframe] = useState("1M");
  const [data, setData] = useState(null);
  const [hidden, setHidden] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    getMarketChart(timeframe).then((d) => { if (active) { setData(d); setLoading(false); } });
    return () => { active = false; };
  }, [timeframe]);

  const indices = data?.indices || [];
  const series = useMemo(() => data?.series || [], [data]);

  const toggle = (name) => setHidden((h) => ({ ...h, [name]: !h[name] }));

  return (
    <div data-testid="market-overview-chart-container" className="flex h-full flex-col">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="kicker mb-1">Market Overview</div>
          <p className="text-xs text-zinc-500">Relative performance · indexed to period start (%)</p>
        </div>
        <div className="inline-flex items-center rounded-md border border-white/[0.08] bg-[#0B0C0E] p-0.5">
          {TIMEFRAMES.map((tf) => (
            <button
              key={tf}
              data-testid={`market-overview-timeframe-${tf.toLowerCase()}`}
              onClick={() => setTimeframe(tf)}
              className={`lv-press rounded px-2.5 py-1 font-mono text-[11px] transition-colors ${
                timeframe === tf ? "bg-[#1E222A] text-white shadow-sm" : "text-zinc-500 hover:text-zinc-200"
              }`}
            >
              {tf}
            </button>
          ))}
        </div>
      </div>

      <div className="relative min-h-[280px] flex-1">
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="font-mono text-xs text-zinc-600">Loading series…</span>
          </div>
        )}
        <ResponsiveContainer width="100%" height="100%" minHeight={280}>
          <LineChart data={series} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
            <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
            <XAxis dataKey="t" tick={{ fill: "#52525B", fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={{ stroke: "rgba(255,255,255,0.08)" }} tickLine={false} minTickGap={40} />
            <YAxis tick={{ fill: "#52525B", fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} tickFormatter={(v) => `${v > 0 ? "+" : ""}${v}%`} width={46} />
            <Tooltip content={<ChartTooltip />} cursor={{ stroke: "rgba(255,255,255,0.2)", strokeDasharray: "3 3" }} />
            {indices.map((idx) => (
              !hidden[idx.name] && (
                <Line
                  key={idx.name}
                  type="monotone"
                  dataKey={idx.name}
                  stroke={idx.color}
                  strokeWidth={idx.name === "NIFTY 50" ? 2 : 1.4}
                  dot={false}
                  activeDot={{ r: 3, strokeWidth: 0 }}
                  isAnimationActive={true}
                  animationDuration={600}
                />
              )
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-white/[0.06] pt-3">
        {indices.map((idx) => (
          <button
            key={idx.name}
            onClick={() => toggle(idx.name)}
            className={`flex items-center gap-1.5 font-mono text-[11px] transition-opacity ${hidden[idx.name] ? "opacity-35" : "opacity-100"}`}
          >
            <span className="h-2 w-3 rounded-sm" style={{ background: idx.color }} />
            <span className="text-zinc-300">{idx.name}</span>
          </button>
        ))}
      </div>
    </div>
  );
};
